import type {WizardData} from '../index'
import {levelIdKey, subjectIdKey} from './levelCurriculum'

type ManualData = WizardData['manualData']

/**
 * Curriculum config persisted into user preferences (OnboardingWizard.curriculum).
 *
 * The GET /onboarding/data response groups levels directly under a branch and carries NO
 * curriculum, but the wizard organises Levels/Classes/Subjects under a curriculum
 * (`curriculums[]` + `levelsByCurriculum[name]`). So the curriculum→level association is saved
 * to preferences as the user builds it, then joined back with /onboarding/data on resume.
 */
export type OnboardingCurriculumConfig = {
  curriculums: string[]
  /** API code by curriculum name (see `manualData.curriculumCodes`); absent for legacy picks. */
  curriculumCodes?: Record<string, string>
  /** Short code by curriculum name (see `manualData.curriculumShortCodes`). */
  curriculumShortCodes?: Record<string, string>
  /** Names typed under "Other", selected or not (see `manualData.customCurriculums`). */
  customCurriculums?: string[]
  /** "<country>|<code>" that last seeded Institution Setup (see `manualData.institutionPrefillKey`). */
  institutionPrefillKey?: string
  levelsByCurriculum: Record<
    string,
    {
      levels: Array<{name: string; id?: string}>
      levelsSameAcrossBranches: boolean | null
      levelsPerBranch?: Record<string, string[]>
    }
  >
}

// Fallback curriculum name used when preferences carry no saved curriculum (legacy users
// onboarded before curriculum was persisted, or a user who skipped the Setup Basics
// curriculum picker). Prefs-driven curriculums always win.
export const FALLBACK_CURRICULUM = 'Default Curriculum'

const toTrimmedString = (value: unknown): string => String(value ?? '').trim()

/** The curriculum-naming fields shared by a saved config and the live manualData. */
type CurriculumNaming = {
  curriculums?: readonly unknown[]
  customCurriculums?: readonly unknown[]
  levelsByCurriculum?: Record<string, unknown>
}

/** True when the config names at least one curriculum that is not the placeholder. */
export const hasRealCurriculum = (config: CurriculumNaming | null | undefined): boolean =>
  (config?.curriculums ?? []).some((name) => {
    const clean = toTrimmedString(name)
    return clean.length > 0 && clean !== FALLBACK_CURRICULUM
  }) ||
  Object.keys(config?.levelsByCurriculum ?? {}).some(
    (name) => toTrimmedString(name).length > 0 && toTrimmedString(name) !== FALLBACK_CURRICULUM
  )

/**
 * True when the config is nothing but the placeholder: the shape a re-hydration produces when
 * it ran without the saved curriculum. Saving it would replace the real curriculum for good.
 */
export const isPlaceholderCurriculumConfig = (
  config: CurriculumNaming | null | undefined
): boolean => {
  const curriculums = (config?.curriculums ?? []).map(toTrimmedString).filter(Boolean)
  return (
    curriculums.length > 0 &&
    !hasRealCurriculum(config) &&
    (config?.customCurriculums ?? []).length === 0
  )
}

const toPositiveNumber = (value: unknown): number | null => {
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

const slugId = (name: string): string =>
  `lvl-${toTrimmedString(name)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')}`

/**
 * Slim, preferences-safe copy of the curriculum structure the user has entered. Drops the heavy
 * `levelDetails` (classes/subjects) since those are re-derived from /onboarding/data on resume.
 */
export const extractCurriculumConfig = (manualData: ManualData): OnboardingCurriculumConfig => {
  const curriculums = Array.isArray(manualData?.curriculums)
    ? manualData.curriculums.map(toTrimmedString).filter(Boolean)
    : []
  const source = (manualData?.levelsByCurriculum ?? {}) as Record<string, any>
  const levelsByCurriculum: OnboardingCurriculumConfig['levelsByCurriculum'] = {}

  Object.entries(source).forEach(([curriculum, cfg]) => {
    const levels = (Array.isArray(cfg?.levels) ? cfg.levels : [])
      .map((level: any) => {
        const name = toTrimmedString(level?.name)
        return name ? {name, ...(level?.id ? {id: String(level.id)} : {})} : null
      })
      .filter(Boolean) as Array<{name: string; id?: string}>

    levelsByCurriculum[curriculum] = {
      levels,
      levelsSameAcrossBranches: cfg?.levelsSameAcrossBranches ?? true,
      ...(cfg?.levelsPerBranch ? {levelsPerBranch: cfg.levelsPerBranch} : {}),
    }
  })

  const curriculumCodes: Record<string, string> = {}
  Object.entries(manualData?.curriculumCodes ?? {}).forEach(([name, code]) => {
    const key = toTrimmedString(name)
    const value = toTrimmedString(code)
    if (key && value && curriculums.includes(key)) curriculumCodes[key] = value
  })
  const curriculumShortCodes: Record<string, string> = {}
  Object.entries(manualData?.curriculumShortCodes ?? {}).forEach(([name, shortCode]) => {
    const key = toTrimmedString(name)
    const value = toTrimmedString(shortCode)
    if (key && value && curriculums.includes(key)) curriculumShortCodes[key] = value
  })

  const customCurriculums = Array.isArray(manualData?.customCurriculums)
    ? Array.from(new Set(manualData.customCurriculums.map(toTrimmedString).filter(Boolean)))
    : []

  const institutionPrefillKey = toTrimmedString(manualData?.institutionPrefillKey)

  return {
    curriculums,
    ...(Object.keys(curriculumCodes).length > 0 ? {curriculumCodes} : {}),
    ...(Object.keys(curriculumShortCodes).length > 0 ? {curriculumShortCodes} : {}),
    ...(customCurriculums.length > 0 ? {customCurriculums} : {}),
    ...(institutionPrefillKey ? {institutionPrefillKey} : {}),
    levelsByCurriculum,
  }
}

/**
 * The onboarding sections that GET /onboarding/data does NOT return (duration, timings, venues,
 * frequency, students). These are persisted into user preferences (OnboardingWizard.setup) as the
 * user fills them, then merged back on resume so those steps come back pre-filled too.
 */
export type OnboardingSetup = Pick<
  ManualData,
  | 'branches'
  | 'durationName'
  | 'durationStartDate'
  | 'durationEndDate'
  | 'timings'
  | 'venuesSameForClass'
  | 'venuesAutoCount'
  | 'venues'
  | 'defaultSubjectFrequency'
  | 'defaultFrequencyPer'
  | 'students'
  | 'studentsCount'
  | 'deletedStudents'
> & {break_timings?: ManualData['break_timings']}

/**
 * The `manualData` fields serialised into `OnboardingWizard.setup`, in write order.
 *
 * Also the exact set of fields the wizard tracks "touched" state for: the backend reads this
 * bucket as "the user entered data on that screen", so a field only belongs in it once the user
 * has actually edited it (see `extractOnboardingSetup`). `studentsCount` is absent deliberately —
 * it is derived from `students`, never edited.
 */
export const ONBOARDING_SETUP_FIELDS = [
  'branches',
  'durationName',
  'durationStartDate',
  'durationEndDate',
  'timings',
  'break_timings',
  'venuesSameForClass',
  'venuesAutoCount',
  'venues',
  'defaultSubjectFrequency',
  'defaultFrequencyPer',
  'students',
  // Pending student deletes: kept with the students so a logout before Next can't lose them.
  'deletedStudents',
] as const

/**
 * Slim, preferences-safe copy of the non-API onboarding sections (Step 2 duration/timings/venues/
 * frequency + Step 5 students). Empty/undefined fields are dropped so the saved object stays small
 * and a merge never clobbers existing values with blanks.
 *
 * `touchedFields` restricts the result to fields the user has actually edited. This bucket is not
 * only a resume cache: node-api infers the onboarding funnel's "saved" rows from it, one per
 * (tenant, sub-step), deduplicated forever — so a value the wizard pre-filled for the user would
 * permanently stamp that sub-step as saved before the user did anything, and every later genuine
 * save on that screen would be discarded as a duplicate. Omitting the argument serialises
 * everything present (used where the caller already knows the data is user-entered).
 */
export const extractOnboardingSetup = (
  manualData: ManualData,
  touchedFields?: ReadonlySet<string>
): OnboardingSetup => {
  const setup: Record<string, unknown> = {}
  const source = (manualData ?? {}) as Record<string, unknown>

  ONBOARDING_SETUP_FIELDS.forEach((key) => {
    if (touchedFields && !touchedFields.has(key)) return
    const value = source[key]
    if (value === undefined || value === null) return
    if (Array.isArray(value) && value.length === 0) return
    if (typeof value === 'string' && value.trim().length === 0) return
    setup[key] = value
  })

  const students = setup.students
  if (Array.isArray(students) && students.length > 0) {
    setup.studentsCount = students.length
  }

  return setup as OnboardingSetup
}

/**
 * Draft of the in-progress Classes sub-step (OnboardingWizard.classesDraft).
 *
 * Classes are only persisted to the backend once the user finishes the Review sub-screen, so a
 * user who fills classes and leaves at the Sections sub-step (before Review) has nothing to
 * restore from GET /onboarding/data. This draft captures the class list (which lives in
 * `levelsByCurriculum[].levelDetails[].classes`, stripped from the curriculum bucket), the section
 * config, and the flow position so that specific case comes back pre-filled. It is a FALLBACK
 * only — on resume the backend still wins whenever it actually returns classes.
 */
export type OnboardingClassesDraft = {
  classes?: ManualData['classes']
  classesCount?: ManualData['classesCount']
  classesSectionCount?: ManualData['classesSectionCount']
  classesSectionNomenclature?: ManualData['classesSectionNomenclature']
  classesFlowStep?: ManualData['classesFlowStep']
  classesReviewRowsByBranch?: unknown
  levelsByCurriculum?: ManualData['levelsByCurriculum']
  /**
   * Deletes queued on Academic Structure but not yet sent (they go out on that step's Next).
   * Persisted so a logout before Next keeps them; on resume they are applied to the hydrated
   * data (applyPendingDeletes) and stay queued for the next sync. Unlike the rest of the draft
   * these are NOT a fallback: the backend still holds what they name, that is the point.
   */
  deletedClasses?: ManualData['deletedClasses']
  deletedLevels?: ManualData['deletedLevels']
}

// True when the user has entered at least one class base name (Classes screen) somewhere in the
// curriculum structure. This is the earliest signal that the Classes sub-step has real work worth
// preserving, since the flattened `classes[]` rows are not built until after the Sections screen.
const levelDetailsHasClasses = (levelsByCurriculum: unknown): boolean => {
  const source = (levelsByCurriculum ?? {}) as Record<string, any>
  return Object.values(source).some((cfg: any) =>
    Object.values(cfg?.levelDetails ?? {}).some(
      (ld: any) => Array.isArray(ld?.classes) && ld.classes.length > 0
    )
  )
}

/**
 * Snapshot of the classes-in-progress fields, or `{}` when there is nothing worth persisting yet.
 * Kept only once the user has actually entered classes (base names or flattened rows) so we never
 * write an empty draft that a later merge could mistake for real data.
 */
export const extractClassesDraft = (manualData: ManualData): OnboardingClassesDraft => {
  const classes = Array.isArray(manualData?.classes) ? manualData.classes : []
  const levelsByCurriculum = manualData?.levelsByCurriculum
  const deletedClasses = Array.isArray(manualData?.deletedClasses) ? manualData.deletedClasses : []
  const deletedLevels = Array.isArray(manualData?.deletedLevels) ? manualData.deletedLevels : []
  const hasPendingDeletes = deletedClasses.length > 0 || deletedLevels.length > 0
  if (classes.length === 0 && !levelDetailsHasClasses(levelsByCurriculum) && !hasPendingDeletes) {
    return {}
  }

  const draft: OnboardingClassesDraft = {}
  if (deletedClasses.length > 0) draft.deletedClasses = deletedClasses
  if (deletedLevels.length > 0) draft.deletedLevels = deletedLevels
  if (classes.length > 0) {
    draft.classes = classes
    if (typeof manualData?.classesCount === 'number') draft.classesCount = manualData.classesCount
  }
  if (manualData?.classesSectionCount) draft.classesSectionCount = manualData.classesSectionCount
  if (manualData?.classesSectionNomenclature) {
    draft.classesSectionNomenclature = manualData.classesSectionNomenclature
  }
  if (typeof manualData?.classesFlowStep === 'number') {
    draft.classesFlowStep = manualData.classesFlowStep
  }
  const reviewRows = (manualData as any)?.classesReviewRowsByBranch
  if (reviewRows && typeof reviewRows === 'object' && Object.keys(reviewRows).length > 0) {
    draft.classesReviewRowsByBranch = reviewRows
  }
  if (levelsByCurriculum && Object.keys(levelsByCurriculum).length > 0) {
    draft.levelsByCurriculum = levelsByCurriculum
  }
  return draft
}

/**
 * Merges a saved classes draft onto the resume-hydrated manualData. Called ONLY when the backend
 * returned no classes (the draft is a fallback, never an override of real server data). The draft's
 * `levelsByCurriculum` carries the `levelDetails` (class base names) that the stripped curriculum
 * bucket dropped, so it fills those in per curriculum/level while keeping the base config's levels.
 */
export const mergeClassesDraft = (base: ManualData, draft: OnboardingClassesDraft): ManualData => {
  const merged: any = {...base}
  if (draft.classes) merged.classes = draft.classes
  if (draft.classesCount != null) merged.classesCount = draft.classesCount
  if (draft.classesSectionCount) merged.classesSectionCount = draft.classesSectionCount
  if (draft.classesSectionNomenclature) {
    merged.classesSectionNomenclature = draft.classesSectionNomenclature
  }
  if (draft.classesFlowStep != null) merged.classesFlowStep = draft.classesFlowStep
  if (draft.classesReviewRowsByBranch) {
    merged.classesReviewRowsByBranch = draft.classesReviewRowsByBranch
  }
  if (draft.levelsByCurriculum) {
    const baseLbc: Record<string, any> = {...(merged.levelsByCurriculum ?? {})}
    Object.entries(draft.levelsByCurriculum).forEach(([curriculum, draftCfg]: [string, any]) => {
      const baseCfg = baseLbc[curriculum] ?? {}
      baseLbc[curriculum] = {
        ...draftCfg,
        ...baseCfg,
        // The draft is the only source of levelDetails (class base names) in this fallback branch.
        levelDetails: {
          ...(baseCfg.levelDetails ?? {}),
          ...(draftCfg.levelDetails ?? {}),
        },
      }
    })
    merged.levelsByCurriculum = baseLbc
  }
  return merged as ManualData
}

const lowerKey = (value: unknown) => toTrimmedString(value).toLowerCase()

/**
 * Backend-hydrated classes with the draft's subjects. Subjects only reach the backend on the
 * Lesson Planning sync, so between Academic Structure's Next and that point GET /onboarding/data
 * returns every class WITHOUT subjects — and the review page said "No subjects added" for levels
 * the user had filled, even though the classes draft in preferences still held them. Fills, never
 * overrides: a grade whose backend subjects are empty takes the draft's, a level missing from
 * `subjectSelectionsByLevel` gains the draft's union, a review row with no subjects takes its
 * draft twin's. Returns `base` untouched when nothing was filled.
 */
export const fillSubjectsFromDraft = (
  base: ManualData,
  draft: OnboardingClassesDraft
): ManualData => {
  const draftLbc = (draft.levelsByCurriculum ?? {}) as Record<string, any>
  const draftRows = (draft.classesReviewRowsByBranch ?? {}) as Record<string, any[]>
  if (Object.keys(draftLbc).length === 0 && Object.keys(draftRows).length === 0) return base

  const merged: any = {...base}
  let changed = false

  const lbc: Record<string, any> = {...(merged.levelsByCurriculum ?? {})}
  const selections: Record<string, string[]> = {...(merged.subjectSelectionsByLevel ?? {})}
  Object.entries(draftLbc).forEach(([curriculum, draftCfg]) => {
    const baseCfg = lbc[curriculum]
    if (!baseCfg) return
    const details: Record<string, any> = {...(baseCfg.levelDetails ?? {})}
    Object.entries((draftCfg?.levelDetails ?? {}) as Record<string, any>).forEach(
      ([level, draftDetail]) => {
        const baseDetail = details[level]
        if (!baseDetail) return
        const draftClasses: any[] = Array.isArray(draftDetail?.classes) ? draftDetail.classes : []
        const draftSubjectsByGrade = new Map<string, string[]>(
          draftClasses.map((cls) => [
            lowerKey(cls?.name),
            ((cls?.subjects ?? []) as unknown[]).map(toTrimmedString).filter(Boolean),
          ])
        )
        const classes = ((baseDetail.classes ?? []) as any[]).map((grade) => {
          if (((grade?.subjects ?? []) as unknown[]).length > 0) return grade
          const fromDraft = draftSubjectsByGrade.get(lowerKey(grade?.name)) ?? []
          if (fromDraft.length === 0) return grade
          changed = true
          return {...grade, subjects: [...fromDraft]}
        })
        details[level] = {...baseDetail, classes}
        const key = `${curriculum}|||${level}`
        if ((selections[key] ?? []).length === 0) {
          const union = Array.from(new Set(draftClasses.flatMap((cls) => cls?.subjects ?? [])))
            .map(toTrimmedString)
            .filter(Boolean)
          if (union.length > 0) {
            selections[key] = union
            changed = true
          }
        }
      }
    )
    lbc[curriculum] = {...baseCfg, levelDetails: details}
  })

  const rowsByBranch: Record<string, any[]> = {}
  Object.entries((merged.classesReviewRowsByBranch ?? {}) as Record<string, any[]>).forEach(
    ([branch, rows]) => {
      const twins: any[] = Array.isArray(draftRows[branch]) ? draftRows[branch] : []
      rowsByBranch[branch] = (rows ?? []).map((row) => {
        if (((row?.subjects ?? []) as unknown[]).length > 0) return row
        const twin = twins.find(
          (candidate) =>
            lowerKey(candidate?.curriculum) === lowerKey(row?.curriculum) &&
            lowerKey(candidate?.level) === lowerKey(row?.level) &&
            lowerKey(candidate?.gradeName) === lowerKey(row?.gradeName)
        )
        const subjects = ((twin?.subjects ?? []) as unknown[]).map(toTrimmedString).filter(Boolean)
        if (subjects.length === 0) return row
        changed = true
        return {...row, subjects}
      })
    }
  )

  if (!changed) return base
  merged.levelsByCurriculum = lbc
  merged.subjectSelectionsByLevel = selections
  merged.classesReviewRowsByBranch = rowsByBranch
  return merged as ManualData
}

/**
 * Maps a GET /onboarding/data response (+ the curriculum config saved in preferences) into the
 * wizard's `manualData` shape so completed steps come back pre-filled.
 *
 * Response shape (result):
 *   campuses[]: {branch_id, branch, levels[]}
 *     levels[]: {level_id, level, grades[]}
 *       grades[]: {grade_id, grade, sections[]}
 *         sections[]: {section_id, section, subjects[]}
 *           subjects[]: {subject_id, name, teacher_id}
 *   teachers[]
 *
 * Only the sections present in the response are populated (branches, curriculums, levels, classes,
 * subjects, teachers). Duration/timings/venues/students are not part of this endpoint.
 */
export const mapOnboardingDataToManualData = (
  apiResult: any,
  curriculumConfig: OnboardingCurriculumConfig | null,
  baseManualData: ManualData
): Partial<ManualData> => {
  const result = apiResult?.result ?? apiResult?.data ?? apiResult ?? {}
  const campuses: any[] = Array.isArray(result?.campuses) ? result.campuses : []
  if (campuses.length === 0) return {}

  // --- Level -> curriculum lookup from the saved config -----------------------------------
  // The saved (preferences) config is authoritative, but it can be empty — or only the
  // placeholder — for a school that picked its curriculum in THIS visit: the portal's copy of
  // preferences is read once on mount. The in-memory wizard state then knows the real
  // curriculum and must win, or a re-hydration (after the Excel import) relabels every level
  // "Default Curriculum", empties the allocation grid, and that placeholder gets saved back.
  const effectiveConfig: OnboardingCurriculumConfig | null = hasRealCurriculum(curriculumConfig)
    ? curriculumConfig
    : hasRealCurriculum(baseManualData)
      ? extractCurriculumConfig(baseManualData)
      : curriculumConfig
  const configCurriculums = (effectiveConfig?.curriculums ?? [])
    .map(toTrimmedString)
    .filter(Boolean)
  const configLBC = effectiveConfig?.levelsByCurriculum ?? {}
  const levelToCurriculum = new Map<string, string>()
  Object.entries(configLBC).forEach(([curriculum, cfg]) => {
    ;(cfg?.levels ?? []).forEach((level: any) => {
      const name = toTrimmedString(level?.name).toLowerCase()
      if (name) levelToCurriculum.set(name, curriculum)
    })
    Object.values(cfg?.levelsPerBranch ?? {}).forEach((names: any) => {
      ;(Array.isArray(names) ? names : []).forEach((name: any) => {
        const key = toTrimmedString(name).toLowerCase()
        if (key) levelToCurriculum.set(key, curriculum)
      })
    })
  })
  const resolveCurriculum = (levelName: string): string => {
    const hit = levelToCurriculum.get(levelName.toLowerCase())
    if (hit) return hit
    if (configCurriculums.length > 0) return configCurriculums[0]
    return FALLBACK_CURRICULUM
  }
  // Schools whose backend stores the curriculum on each level send it per level (FE-4): two
  // levels can then share a name ("Primary" under FBISE and Cambridge), so the level's own
  // curriculum wins over the name lookup above. It is the wizard's display name; matched to
  // the config's spelling, and used as-is only when the config names no real curriculum. A
  // level with `curriculum: null` (saved before curricula were stored) keeps the name lookup.
  const configHasRealCurriculum = hasRealCurriculum(effectiveConfig)
  const configCurriculumByLower = new Map(
    [...configCurriculums, ...Object.keys(configLBC)].map((name) => [
      toTrimmedString(name).toLowerCase(),
      toTrimmedString(name),
    ])
  )
  const serverCurriculumOf = (level: any): string | null => {
    const name = toTrimmedString(level?.curriculum)
    if (!name) return null
    const configured = configCurriculumByLower.get(name.toLowerCase())
    if (configured) return configured
    return configHasRealCurriculum ? null : name
  }
  const curriculumCodes: Record<string, string> = {...(baseManualData?.curriculumCodes ?? {})}
  const curriculumShortCodes: Record<string, string> = {
    ...(baseManualData?.curriculumShortCodes ?? {}),
  }

  // --- Defaults for subject frequency/duration (from Step 2) ------------------------------
  const defaultFreqNum = baseManualData?.defaultSubjectFrequency
  const defaultPer = baseManualData?.defaultFrequencyPer || 'week'
  const defaultFrequencyStr =
    defaultFreqNum && defaultFreqNum >= 1 ? `${defaultFreqNum}/${defaultPer}` : '1/week'
  const defaultDurationStr = '1'

  // --- Teacher lookup (for lesson-planning teacher assignments) ---------------------------
  const teachersRaw: any[] = Array.isArray(result?.teachers) ? result.teachers : []
  const teacherById = new Map<number, any>()
  teachersRaw.forEach((teacher: any) => {
    const id = toPositiveNumber(teacher?.teacher_id ?? teacher?.id)
    if (id) teacherById.set(id, teacher)
  })

  // --- Accumulators -----------------------------------------------------------------------
  const branches: string[] = []
  const classes: any[] = []
  const subjects: any[] = []
  const subjectIdByNameAndClass: Record<string, number> = {}
  const selectionSets: Record<string, Set<string>> = {} // `${curriculum}|||${level}` -> subject names
  const levelDetailsByCurriculum: Record<
    string,
    Record<string, {classes: Array<{name: string; subjects: string[]}>}>
  > = {}
  const classesReviewRowsByBranch: Record<string, any[]> = {}
  // Server ids for branches and levels, the same shape the bulk-import response merge keeps
  // (bulkImportIdMerge.ts). Without them a level removed after a resume has no id to delete by.
  const branchIdByName: Record<string, number> = {}
  const branchLevelIdMap: Record<string, Record<string, number>> = {}
  const curriculumLevelNames: Record<string, Set<string>> = {}
  const curriculumLevelsPerBranch: Record<string, Record<string, Set<string>>> = {}
  const lessonPlanningData: Record<string, Record<string, any>> = {}
  // Global section labels (A, B, ...) drive the Sections step's count + nomenclature. A "class" in
  // the wizard is a grade base name (e.g. "Early Years"); its sections are configured separately.
  const allSectionNames = new Set<string>()

  campuses.forEach((campus) => {
    const branchName = toTrimmedString(campus?.branch) || 'Main Branch'
    if (!branches.includes(branchName)) branches.push(branchName)
    const branchId = toPositiveNumber(campus?.branch_id)
    if (branchId && !branchIdByName[branchName]) branchIdByName[branchName] = branchId
    const reviewRows = (classesReviewRowsByBranch[branchName] ??= [])
    const levels: any[] = Array.isArray(campus?.levels) ? campus.levels : []

    levels.forEach((level) => {
      const levelName = toTrimmedString(level?.level)
      if (!levelName) return
      const serverCurriculum = serverCurriculumOf(level)
      const curriculum = serverCurriculum ?? resolveCurriculum(levelName)
      if (serverCurriculum) {
        const code = toTrimmedString(level?.curriculum_code)
        const shortCode = toTrimmedString(level?.curriculum_short_code)
        if (code && !curriculumCodes[serverCurriculum]) curriculumCodes[serverCurriculum] = code
        if (shortCode && !curriculumShortCodes[serverCurriculum]) {
          curriculumShortCodes[serverCurriculum] = shortCode
        }
      }
      const levelId = toPositiveNumber(level?.level_id)
      if (levelId) {
        const branchLevelIds = (branchLevelIdMap[branchName] ??= {})
        // Keyed by curriculum when the server says which one; the bare name as well, which the
        // payload builder only trusts while no other curriculum shares the name.
        if (serverCurriculum) branchLevelIds[levelIdKey(levelName, serverCurriculum)] ??= levelId
        branchLevelIds[levelName] ??= levelId
      }

      ;(curriculumLevelNames[curriculum] ??= new Set()).add(levelName)
      const perBranch = (curriculumLevelsPerBranch[curriculum] ??= {})
      ;(perBranch[branchName] ??= new Set()).add(levelName)
      const levelDetails = (levelDetailsByCurriculum[curriculum] ??= {})
      const levelDetail = (levelDetails[levelName] ??= {classes: []})
      const selectionKey = `${curriculum}|||${levelName}`
      const selectionSet = (selectionSets[selectionKey] ??= new Set())

      const grades: any[] = Array.isArray(level?.grades) ? level.grades : []
      grades.forEach((grade) => {
        const gradeName = toTrimmedString(grade?.grade)
        if (!gradeName) return
        const sections: any[] = Array.isArray(grade?.sections) ? grade.sections : []
        // A grade with no sections has no classes: the backend keeps the grade node after all its
        // classes are deleted, so treat it as deleted — no levelDetail entry, no review row.
        // Otherwise a deleted grade resurfaces in the Review table on resume.
        if (sections.length === 0) return
        const rowSections: string[] = []
        const rowSubjects = new Set<string>()

        sections.forEach((section) => {
          const sectionName = toTrimmedString(section?.section)
          if (!sectionName) return
          rowSections.push(sectionName)
          allSectionNames.add(sectionName)
          const className = `${gradeName}-${sectionName}`

          classes.push({
            branch: branchName,
            level: levelName,
            curriculum,
            grade: gradeName,
            section: sectionName,
            teacher: '',
            name: className,
            // The endpoint returns no class id; this marks the row as persisted so removing
            // it still queues a delete (see applyAcademicStructure / deletedClasses).
            persisted: true,
          })

          const sectionSubjects: any[] = Array.isArray(section?.subjects) ? section.subjects : []
          sectionSubjects.forEach((subject) => {
            const subjectName = toTrimmedString(subject?.name)
            if (!subjectName) return
            selectionSet.add(subjectName)
            rowSubjects.add(subjectName)

            const subjectId = toPositiveNumber(subject?.subject_id)
            if (subjectId) {
              subjectIdByNameAndClass[subjectIdKey(subjectName, className, serverCurriculum)] =
                subjectId
            }

            subjects.push({
              name: subjectName,
              curriculum,
              level: levelName,
              branch: branchName,
              class: className,
              teacher: '',
              frequency: '',
              duration: '',
              ...(subjectId ? {id: subjectId} : {}),
            })

            // Teacher already assigned to this class-subject -> seed the lesson-planning grid.
            const teacherId = toPositiveNumber(subject?.teacher_id)
            const teacher = teacherId ? teacherById.get(teacherId) : null
            if (teacher) {
              const classKey = `${branchName}|||${curriculum}|||${className}`
              const cellMap = (lessonPlanningData[classKey] ??= {})
              cellMap[subjectName] = {
                teacher: `${toTrimmedString(teacher?.first_name)} ${toTrimmedString(
                  teacher?.last_name
                )}`.trim(),
                teacherEmployeeId: toTrimmedString(
                  teacher?.employeeId ?? teacher?.employee_id ?? teacher?.last_name
                ),
                frequency: defaultFrequencyStr,
                duration: defaultDurationStr,
              }
            }
          })
        })

        // One "class" per grade base name (not per grade-section); subjects are the union across
        // the grade's sections. Merge if the same grade recurs (e.g. across branches).
        const gradeSubjects = Array.from(rowSubjects)
        const existingClass = levelDetail.classes.find((entry) => entry.name === gradeName)
        if (existingClass) {
          existingClass.subjects = Array.from(
            new Set([...existingClass.subjects, ...gradeSubjects])
          )
        } else {
          levelDetail.classes.push({name: gradeName, subjects: gradeSubjects})
        }

        reviewRows.push({
          id: `${branchName}-${curriculum}-${levelName}-${gradeName}`,
          curriculum,
          level: levelName,
          gradeName,
          sections: rowSections,
          subjects: gradeSubjects,
        })
      })
    })
  })

  // --- subjectSelectionsByLevel -----------------------------------------------------------
  // Only emit levels that actually have saved subjects. An EMPTY entry (a level the server
  // returned before any subjects were imported) would be read as "user chose no subjects" and
  // mask the school-system-details defaults on the Subjects step, leaving it un-prefilled.
  const subjectSelectionsByLevel: Record<string, string[]> = {}
  Object.entries(selectionSets).forEach(([key, set]) => {
    if (set.size > 0) subjectSelectionsByLevel[key] = Array.from(set)
  })

  // --- Dedupe subjects (name|||class|||branch|||level) ------------------------------------
  const dedupedSubjects: any[] = []
  const seenSubjects = new Set<string>()
  subjects.forEach((subject) => {
    const key = `${subject.name}|||${subject.class}|||${subject.branch}|||${subject.level}|||${subject.curriculum}`
    if (!seenSubjects.has(key)) {
      seenSubjects.add(key)
      dedupedSubjects.push(subject)
    }
  })

  // --- levelsByCurriculum (config levels/branch layout + rebuilt levelDetails) ------------
  const levelsByCurriculum: Record<string, any> = {}
  const allCurriculums = new Set<string>([
    ...configCurriculums,
    ...Object.keys(curriculumLevelNames),
  ])
  allCurriculums.forEach((curriculum) => {
    const cfg = configLBC[curriculum]
    const builtLevelNames = Array.from(curriculumLevelNames[curriculum] ?? [])

    const levels: Array<{name: string; id: string}> =
      Array.isArray(cfg?.levels) && cfg.levels.length > 0
        ? cfg.levels.map((level: any) => {
            const name = toTrimmedString(level?.name)
            return {name, id: level?.id ? String(level.id) : slugId(name)}
          })
        : builtLevelNames.map((name) => ({name, id: slugId(name)}))
    builtLevelNames.forEach((name) => {
      if (!levels.find((level) => level.name === name)) levels.push({name, id: slugId(name)})
    })

    let levelsPerBranch: Record<string, string[]> = {}
    if (cfg?.levelsPerBranch && Object.keys(cfg.levelsPerBranch).length > 0) {
      levelsPerBranch = {...cfg.levelsPerBranch}
    } else {
      Object.entries(curriculumLevelsPerBranch[curriculum] ?? {}).forEach(([branch, set]) => {
        levelsPerBranch[branch] = Array.from(set)
      })
    }

    levelsByCurriculum[curriculum] = {
      levels,
      levelsSameAcrossBranches: cfg?.levelsSameAcrossBranches ?? branches.length <= 1,
      levelsPerBranch,
      levelDetails: levelDetailsByCurriculum[curriculum] ?? {},
    }
  })

  const curriculums =
    configCurriculums.length > 0 ? [...configCurriculums] : Array.from(allCurriculums)
  const levelsCount = Object.values(levelsByCurriculum).reduce(
    (sum: number, cfg: any) => sum + (Array.isArray(cfg?.levels) ? cfg.levels.length : 0),
    0
  )

  // --- Teachers ---------------------------------------------------------------------------
  const teachers = teachersRaw.map((teacher: any) => {
    const fullFirst = toTrimmedString(teacher?.first_name)
    const rawLast = toTrimmedString(teacher?.last_name)
    const employeeId = toTrimmedString(teacher?.employeeId ?? teacher?.employee_id ?? rawLast)
    // API payloads flatten the name (first_name = full name, last_name = employeeId). Recover a
    // real first/last split when we detect that flattening; otherwise keep as-is.
    let first_name = fullFirst
    let last_name = rawLast
    if (rawLast && rawLast === employeeId) {
      const parts = fullFirst.split(/\s+/)
      first_name = parts[0] ?? ''
      last_name = parts.slice(1).join(' ')
    }
    const id = toPositiveNumber(teacher?.teacher_id ?? teacher?.id)
    return {
      ...(id ? {id} : {}),
      first_name,
      last_name,
      employeeId,
      level: toTrimmedString(teacher?.level),
      ...(serverCurriculumOf(teacher) ? {curriculum: serverCurriculumOf(teacher)} : {}),
      email: toTrimmedString(teacher?.email),
      cnic: toTrimmedString(teacher?.cnic),
    }
  })

  // Section config for the Sections step: labels sorted naturally (A, B, ... / 1, 2, ...).
  const sectionNames = Array.from(allSectionNames).sort((a, b) =>
    a.localeCompare(b, undefined, {numeric: true, sensitivity: 'base'})
  )

  const mapped: Record<string, any> = {
    branches: branches.length > 0 ? branches : (baseManualData?.branches ?? ['Main Branch']),
    curriculums,
    levelsByCurriculum,
    levelsCount,
    classes,
    classesCount: classes.length,
    subjects: dedupedSubjects,
    subjectsCount: dedupedSubjects.length,
    subjectSelectionsByLevel,
    subjectIdByNameAndClass,
    classesReviewRowsByBranch,
    ...(sectionNames.length > 0
      ? {
          classesSectionCount: String(sectionNames.length),
          classesSectionNomenclature: sectionNames.join(','),
        }
      : {}),
    ...(Object.keys(lessonPlanningData).length > 0 ? {lessonPlanningData} : {}),
    ...(Object.keys(branchIdByName).length > 0 ? {branchIdByName} : {}),
    ...(Object.keys(branchLevelIdMap).length > 0 ? {branchLevelIdMap} : {}),
    ...(Object.keys(curriculumCodes).length > 0 ? {curriculumCodes} : {}),
    ...(Object.keys(curriculumShortCodes).length > 0 ? {curriculumShortCodes} : {}),
  }
  if (teachers.length > 0) {
    mapped.teachers = teachers
    mapped.teachersCount = teachers.length
  }

  return mapped as Partial<ManualData>
}
